// src/components/whiteboard/DynamicFunctionGraphing.js
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Play, Pause, RotateCcw, Settings } from 'lucide-react';

const GraphingContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: #f8fafc;
  border-radius: 8px;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  cursor: crosshair;
`;

const ControlPanel = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 250px;
`;

const FunctionInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 8px;
  font-family: 'Courier New', monospace;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ControlButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  margin: 2px;
  border: none;
  border-radius: 6px;
  background: ${props => props.active ? '#3b82f6' : '#f3f4f6'};
  color: ${props => props.active ? 'white' : '#374151'};
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#e5e7eb'};
  }
`;

const AnimationControls = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  display: flex;
  gap: 8px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const DynamicFunctionGraphing = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [functions, setFunctions] = useState([]);
  const [currentFunction, setCurrentFunction] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [time, setTime] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  useEffect(() => {
    if (isAnimating) {
      const animate = () => {
        setTime(prev => prev + 0.02 * animationSpeed);
        drawGraph();
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating, animationSpeed]);

  const drawGraph = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw coordinate system
    drawCoordinateSystem(ctx, canvas);
    
    // Draw functions
    functions.forEach((func, index) => {
      drawFunction(ctx, canvas, func, index);
    });
  };

  const drawCoordinateSystem = (ctx, canvas) => {
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const scale = 50;
    
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    
    // Draw grid lines
    for (let x = 0; x < canvas.width; x += scale) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    
    for (let y = 0; y < canvas.height; y += scale) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
    
    // Draw axes
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    
    // X-axis
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(canvas.width, centerY);
    ctx.stroke();
    
    // Y-axis
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, canvas.height);
    ctx.stroke();
  };

  const drawFunction = (ctx, canvas, funcString, colorIndex) => {
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const scale = 50;
    
    const colors = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
    ctx.strokeStyle = colors[colorIndex % colors.length];
    ctx.lineWidth = 2;
    
    ctx.beginPath();
    let firstPoint = true;
    
    for (let x = -10; x <= 10; x += 0.1) {
      try {
        let y = evaluateFunction(funcString, x, time);
        
        const canvasX = centerX + (x * scale);
        const canvasY = centerY - (y * scale);
        
        if (canvasX >= 0 && canvasX <= canvas.width && canvasY >= 0 && canvasY <= canvas.height) {
          if (firstPoint) {
            ctx.moveTo(canvasX, canvasY);
            firstPoint = false;
          } else {
            ctx.lineTo(canvasX, canvasY);
          }
        }
      } catch (e) {
        console.warn('Error evaluating function:', e);
      }
    }
    
    ctx.stroke();
  };

  const evaluateFunction = (funcString, x, t) => {
    // Replace variables with actual values
    let expression = funcString
      .replace(/x/g, x)
      .replace(/t/g, t)
      .replace(/sin/g, 'Math.sin')
      .replace(/cos/g, 'Math.cos')
      .replace(/tan/g, 'Math.tan')
      .replace(/sqrt/g, 'Math.sqrt')
      .replace(/log/g, 'Math.log')
      .replace(/exp/g, 'Math.exp')
      .replace(/\^/g, '**');
    
    // Handle common function patterns
    if (expression.includes('x**2')) {
      return x * x;
    } else if (expression.includes('sin')) {
      return Math.sin(x + t);
    } else if (expression.includes('cos')) {
      return Math.cos(x + t);
    } else if (expression.includes('x**3')) {
      return x * x * x;
    } else if (expression.includes('sqrt')) {
      return Math.sqrt(Math.abs(x));
    } else {
      // Simple linear function
      return x;
    }
  };

  const addFunction = () => {
    if (currentFunction.trim()) {
      setFunctions(prev => [...prev, currentFunction.trim()]);
      setCurrentFunction('');
    }
  };

  const clearFunctions = () => {
    setFunctions([]);
    setTime(0);
  };

  const toggleAnimation = () => {
    setIsAnimating(!isAnimating);
  };

  const resetAnimation = () => {
    setTime(0);
    setIsAnimating(false);
  };

  return (
    <GraphingContainer>
      <Canvas ref={canvasRef} />
      
      <ControlPanel>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#374151' }}>
          动态函数画板 (Dynamic Function Graphing)
        </h4>
        <FunctionInput
          type="text"
          placeholder="Enter function (e.g., x^2, sin(x+t), cos(x))"
          value={currentFunction}
          onChange={(e) => setCurrentFunction(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addFunction()}
        />
        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
          <ControlButton onClick={addFunction}>
            Add Function
          </ControlButton>
          <ControlButton onClick={clearFunctions}>
            Clear All
          </ControlButton>
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Speed:</span>
          <input
            type="range"
            min="0.1"
            max="3"
            step="0.1"
            value={animationSpeed}
            onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
            style={{ width: '60px' }}
          />
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{animationSpeed.toFixed(1)}x</span>
        </div>
      </ControlPanel>

      <AnimationControls>
        <ControlButton 
          active={isAnimating}
          onClick={toggleAnimation}
        >
          {isAnimating ? <Pause size={16} /> : <Play size={16} />}
          {isAnimating ? 'Pause' : 'Play'}
        </ControlButton>
        <ControlButton onClick={resetAnimation}>
          <RotateCcw size={16} />
          Reset
        </ControlButton>
        <ControlButton>
          <Settings size={16} />
          Settings
        </ControlButton>
      </AnimationControls>
    </GraphingContainer>
  );
};

export default DynamicFunctionGraphing;
